import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Link2, Paperclip, Pencil, Plus, Star, Upload, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { useCandidatesQuery, useJobsQuery, useOpenSigned, useUpsertCandidateMutation } from '@/client/api/hr/records';
import { CANDIDATE_STAGES, CANDIDATE_STAGE_LABEL, type CandidateStageV2, type CandidateV2 } from '@/client/types/hr';
import { EmptyState, RowMenu, StatusBadge } from './components/hr-ui';
import { formatDay } from './components/leave-format';
import { JOB_STATUS, JobDialog } from './JobPostingsPage';
import { useEmployees } from './hr-store';

const inputClass = 'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';
const COLUMNS: CandidateStageV2[] = ['applied', 'screen', 'interview', 'offer', 'hired'];

/** One role's candidates, column per stage. */
export default function JobPostingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const job = (useJobsQuery().data ?? []).find((j) => j.id === id);
  const candQ = useCandidatesQuery(id);
  const save = useUpsertCandidateMutation();
  const open = useOpenSigned('hr');
  const employees = useEmployees();
  const canManage = useGate(PERMISSIONS.hr.manage).allowed;
  const fileRef = useRef<HTMLInputElement>(null);
  const [editingJob, setEditingJob] = useState(false);
  const [form, setForm] = useState<(Partial<CandidateV2> & { cv?: File | null }) | null>(null);
  const [linking, setLinking] = useState<CandidateV2 | null>(null);
  const [linkTo, setLinkTo] = useState('');
  const [showRejected, setShowRejected] = useState(false);
  const cands = candQ.data ?? [];
  const byStage = useMemo(() => {
    const m = new Map<CandidateStageV2, CandidateV2[]>();
    cands.forEach((c) => m.set(c.stage, [...(m.get(c.stage) ?? []), c]));
    return m;
  }, [cands]);

  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'People', href: '/admin/human-resource' },
    { label: 'Job postings', href: '/admin/human-resource/recruiting/postings' },
    { label: job?.title ?? 'Posting' },
  ]);

  const move = (c: CandidateV2, stage: CandidateStageV2) =>
    save.mutate({ id: c.id, stage }, { onSuccess: () => toast.success(`${c.name}: ${CANDIDATE_STAGE_LABEL[stage]}`), onError: (e) => toast.error((e as Error).message) });

  const card = (c: CandidateV2) => {
    const idx = CANDIDATE_STAGES.indexOf(c.stage);
    const next = COLUMNS[idx + 1];
    return (
      <div key={c.id} className="rounded-lg border border-gray-100 bg-white p-3 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <button type="button" className="min-w-0 text-left" onClick={() => canManage && setForm(c)}>
            <p className="truncate text-sm font-medium text-gray-900">{c.name}</p>
            <p className="truncate text-xs text-gray-500">{c.email || c.phone || c.source || '—'}</p>
          </button>
          <RowMenu
            items={[
              { label: `Move to ${next ? CANDIDATE_STAGE_LABEL[next] : ''}`, icon: ArrowRight, hidden: !canManage || !next || c.stage === 'hired', onSelect: () => move(c, next) },
              { label: 'Edit', icon: Pencil, hidden: !canManage, onSelect: () => setForm(c) },
              { label: 'Open CV', icon: Paperclip, hidden: !c.hasCv, onSelect: () => open(`/candidates/${c.id}/cv`).catch((e) => toast.error((e as Error).message)) },
              { label: 'Link employee record', icon: Link2, hidden: !canManage || c.stage !== 'hired' || !!c.employeeId, onSelect: () => setLinking(c) },
              { label: 'Reject', icon: XIcon, danger: true, hidden: !canManage || c.stage === 'rejected' || c.stage === 'hired', onSelect: () => move(c, 'rejected') },
            ]}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
          <span className="inline-flex">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`h-3 w-3 ${c.rating && n <= c.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
            ))}
          </span>
          <span>{c.appliedAt ? formatDay(c.appliedAt) : ''}</span>
        </div>
        {c.stage === 'hired' && (
          <p className="mt-1 text-xs text-gray-500">{c.employeeId ? `Employee: ${employees.find((e) => e.id === c.employeeId)?.name ?? 'linked'}` : 'Add them in Directory, then link'}</p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <button type="button" onClick={() => navigate('/admin/human-resource/recruiting/postings')} className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="h-4 w-4" /> Job postings
      </button>
      {!job ? (
        <EmptyState label={candQ.isLoading ? 'Loading…' : 'Posting not found.'} />
      ) : (
        <>
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">{job.title}</h1>
                <StatusBadge label={JOB_STATUS[job.status].label} className={JOB_STATUS[job.status].className} />
              </div>
              <p className="text-sm text-gray-500">
                {job.openings} opening{job.openings === 1 ? '' : 's'}
                {job.location && ` · ${job.location}`}
              </p>
            </div>
            {canManage && (
              <div className="flex gap-2">
                <Button variant="outline" className="gap-2" onClick={() => setEditingJob(true)}>
                  <Pencil className="h-4 w-4" /> Edit posting
                </Button>
                <Button className="gap-2" onClick={() => setForm({ jobPostingId: job.id, name: '', email: '', phone: '', source: '', notes: '' })}>
                  <Plus className="h-4 w-4" /> Add candidate
                </Button>
              </div>
            )}
          </div>

          <div className="grid gap-3 md:grid-cols-5">
            {COLUMNS.map((stage) => (
              <div key={stage} className="rounded-xl bg-gray-100/60 p-2">
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {CANDIDATE_STAGE_LABEL[stage]} ({byStage.get(stage)?.length ?? 0})
                </p>
                <div className="space-y-2">{(byStage.get(stage) ?? []).map(card)}</div>
              </div>
            ))}
          </div>
          {(byStage.get('rejected')?.length ?? 0) > 0 && (
            <div className="mt-4">
              <button type="button" className="text-sm text-gray-500 hover:underline" onClick={() => setShowRejected(!showRejected)}>
                {showRejected ? 'Hide' : 'Show'} rejected ({byStage.get('rejected')?.length})
              </button>
              {showRejected && <div className="mt-2 grid gap-2 md:grid-cols-4">{(byStage.get('rejected') ?? []).map(card)}</div>}
            </div>
          )}
          {job.description && <p className="mt-6 max-w-3xl whitespace-pre-wrap text-sm text-gray-600">{job.description}</p>}
        </>
      )}

      {editingJob && job && <JobDialog job={job} onClose={() => setEditingJob(false)} />}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{form?.id ? form.name : 'Add candidate'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                save.mutate(form, {
                  onSuccess: () => {
                    toast.success('Saved');
                    setForm(null);
                  },
                  onError: (err) => toast.error((err as Error).message || 'Could not save'),
                });
              }}
            >
              {(['name', 'email', 'phone', 'source'] as const).map((key) => (
                <label key={key} className="block space-y-1.5">
                  <span className="text-xs font-medium capitalize text-gray-600">{key === 'source' ? 'Where they came from' : key}</span>
                  <input required={key === 'name'} value={form[key] ?? ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={inputClass} />
                </label>
              ))}
              <div className="flex items-center gap-1">
                <span className="mr-2 text-xs font-medium text-gray-600">Rating</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} aria-label={`${n} stars`}>
                    <Star className={`h-5 w-5 ${form.rating && n <= form.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                  </button>
                ))}
              </div>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Notes</span>
                <textarea rows={3} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" />
              </label>
              <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => setForm({ ...form, cv: e.target.files?.[0] ?? null })} />
              <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => fileRef.current?.click()}>
                <Upload className="h-3.5 w-3.5" /> {form.cv ? form.cv.name : form.hasCv ? 'Replace CV' : 'Attach CV'}
              </Button>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={save.isPending}>
                  Save
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!linking} onOpenChange={(o) => !o && setLinking(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Link {linking?.name}'s employee record</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500">Every employee needs a FeelyFeely account, so add them in People › Directory first, then link the record here.</p>
          <select value={linkTo} onChange={(e) => setLinkTo(e.target.value)} className={inputClass}>
            <option value="">Pick the employee</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => navigate('/admin/human-resource/people/directory')}>
              Open Directory
            </Button>
            <Button
              disabled={!linkTo}
              onClick={() => {
                if (linking)
                  save.mutate({ id: linking.id, employeeId: linkTo }, { onSuccess: () => toast.success('Linked'), onError: (e) => toast.error((e as Error).message) });
                setLinking(null);
                setLinkTo('');
              }}
            >
              Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
